import React, { createContext, useContext, useMemo, useState, useCallback } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";

/**
 * AppShell — root layout for authenticated pages.
 *
 * Structure:
 *   <AppShell sidebar={<Sidebar .../>} topbar={<Topbar .../>}>
 *     {mainChildren}     // typically <PageHeader /> + <PageContainer />
 *   </AppShell>
 *
 * Responsive:
 *   - >= md (900px): sidebar fixed at left, 240px wide, always visible
 *   - < md: sidebar slides in as a drawer with backdrop; topbar hamburger toggles it
 *
 * Context:
 *   Exposes `useAppShellContext()` so child pages (e.g. Topbar) can toggle the
 *   sidebar. Keeps state here rather than threading it through props.
 */

const AppShellContext = createContext({
  isMobile: false,
  sidebarOpen: false,
  openSidebar: () => {},
  closeSidebar: () => {},
  toggleSidebar: () => {},
});

export function useAppShellContext() {
  return useContext(AppShellContext);
}

const SIDEBAR_WIDTH = 240;

function AppShell({ sidebar, topbar, children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const openSidebar = useCallback(() => setSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarOpen((v) => !v), []);

  const ctx = useMemo(
    () => ({ isMobile, sidebarOpen, openSidebar, closeSidebar, toggleSidebar }),
    [isMobile, sidebarOpen, openSidebar, closeSidebar, toggleSidebar]
  );

  return (
    <AppShellContext.Provider value={ctx}>
      <Box
        sx={{
          display: "flex",
          width: "100vw",
          height: "100vh",
          overflow: "hidden",
          backgroundColor: "#f8fafc",
        }}
      >
        {/* Sidebar: static on desktop, drawer + backdrop on mobile */}
        {isMobile ? (
          <>
            {sidebarOpen && (
              <Box
                onClick={closeSidebar}
                sx={{
                  position: "fixed",
                  inset: 0,
                  backgroundColor: "rgba(15, 23, 42, 0.4)",
                  zIndex: 1200,
                  transition: "opacity 150ms ease-out",
                }}
                aria-hidden="true"
              />
            )}
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                bottom: 0,
                width: "min(320px, 100vw)",
                zIndex: 1300,
                backgroundColor: "#ffffff",
                borderRight: "1px solid #e2e8f0",
                transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
                transition: "transform 200ms ease-out",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {sidebar}
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flexShrink: 0,
              width: SIDEBAR_WIDTH,
              height: "100vh",
              backgroundColor: "#ffffff",
              borderRight: "1px solid #e2e8f0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {sidebar}
          </Box>
        )}

        {/* Main column: topbar + scrollable main */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            height: "100vh",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              height: 56,
              borderBottom: "1px solid #e2e8f0",
              backgroundColor: "#ffffff",
              zIndex: 10,
            }}
          >
            {topbar}
          </Box>
          <Box
            component="main"
            sx={{
              flex: 1,
              overflowY: "auto",
              backgroundColor: "#f8fafc",
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </AppShellContext.Provider>
  );
}

export default AppShell;
